/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ItemsModule
 */

import { L10nService } from '@vmw/ngx-vip';
import {
    AviPermissionResource,
    ISystemReport,
    ReportState,
    UpgradeOpsType,
} from 'generated-types';
import { ObjectTypeItem } from 'object-type-item';
import { SystemReport } from 'object-types';
import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';
import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { StringService } from 'string-service';
import * as l10n from './system-reports.item.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

interface ISystemReportData {
    config: ISystemReport;
}

interface ISystemReportStatus {
    shape: string;
    status: string;
    message: string;
}

interface ISystemReportOperation {
    shape: string;
    title: string;
}

/**
 * @description System Report item.
 *
 * @author Deepak Chaudhary
 */
export class SystemReportItem extends withEditChildMessageItemMixin(
    withFullModalMixin(ObjectTypeItem),
) {
    public static ngDependencies = [
        StringService,
        L10nService,
    ];

    /**
     * system report data.
     */
    public data: ISystemReportData;

    public readonly stringService: StringService;

    /**
     * l10nService instance to register source bundles and get keys from source bundles.
     */
    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            permissionName: AviPermissionResource.PERMISSION_CONTROLLER,
            objectName: 'systemreport',
            objectType: SystemReport,
            ...args,
        };

        super(extendedArgs);

        this.stringService = this.getNgDependency(StringService);
        this.l10nService = this.getNgDependency(L10nService);
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Returns Item's Operation icon and name.
     */
    public getOperationIconAndName(): ISystemReportOperation {
        const title = this.config.operation?.split(' ')[0].toUpperCase();
        let shape = '';

        switch (title) {
            case UpgradeOpsType.RESTORE:
                shape = 'backup';
                break;
            case UpgradeOpsType.EVAL_UPGRADE:
            case UpgradeOpsType.UPGRADE_DRYRUN:
            case UpgradeOpsType.UPGRADE:
                shape = 'cog';
                break;
            case UpgradeOpsType.EVAL_PATCH:
            case UpgradeOpsType.EVAL_ROLLBACKPATCH:
            case UpgradeOpsType.PATCH:
            case UpgradeOpsType.ROLLBACKPATCH:
                shape = 'wrench';
                break;
            default:
                shape = 'cog';
                break;
        }

        return {
            shape,
            title,
        };
    }

    /**
     * Returns Item's status icon.
     */
    public getStatus(): ISystemReportStatus {
        const message = this.getConfig().type ?? '';

        switch (this.config.state.config.state) {
            case ReportState.SYSTEM_REPORT_ERROR:
                return {
                    shape: 'exclamation-circle',
                    status: 'danger',
                    message: `${message}
                    ${this.l10nService.getMessage(l10nKeys.reportsFailedLabel)}`,
                };
            case ReportState.SYSTEM_REPORT_WARNING:
                return {
                    shape: 'exclamation-triangle',
                    status: 'warning',
                    message: `${message}
                    ${this.l10nService.getMessage(l10nKeys.reportsWarningLabel)}`,
                };
            case ReportState.SYSTEM_REPORT_SUCCESS:
                return {
                    shape: 'check-circle',
                    status: 'success',
                    message: `${message}
                    ${this.l10nService.getMessage(l10nKeys.reportsSuccessfulLabel)}`,
                };
            case ReportState.SYSTEM_REPORT_IN_PROGRESS:
                return {
                    shape: 'ellipsis-horizontal',
                    status: '',
                    message: `${message}
                    ${this.l10nService.getMessage(l10nKeys.reportsInprogressLabel)}`,
                };
            default:
                return {
                    shape: 'ellipsis-horizontal',
                    status: '',
                    message: `${message}
                    ${this.l10nService.getMessage(l10nKeys.reportsStartedLabel)}`,
                };
        }
    }
}
